import React, { useCallback, useEffect, useState } from 'react';

import { addDays, differenceInDays, parseISO } from 'date-fns';

import { withDateResolver, DatePicker } from 'components/shared/DatePicker';
import { parseDate } from 'components/shared/DatePicker/functions';
import datePickerStyles from 'components/shared/DatePicker/index.module.css';
import { handleFilledDates } from 'utils/filledDates';
import { isEmptyRange } from 'utils/itineraryDetails';

import { determineNextRange, getInitialState } from '../functions';
import styles from '../index.module.css';

const ResolvedDatePicker = withDateResolver(DatePicker);

export const UpdatableDatePicker = ({
  range,
  onChange,
  noRange = false,
  determineSelectedRangeStart,
  getExtraRanges,
}) => {
  const [selectedRange, setSelectedRange] = useState(getInitialState(range));

  const handleUpdateSelectedRange = (newState) => {
    setSelectedRange((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const handleDayClick = useCallback(
    (calendarDayDate) => {
      let newEndDate = selectedRange.end;
      let difference = -differenceInDays(parseISO(selectedRange.start), parseISO(calendarDayDate));

      if (selectedRange.start !== calendarDayDate && newEndDate !== null) {
        newEndDate = new Date(addDays(parseISO(selectedRange.end), difference)).toISOString().substr(0, 10);
      }
      const newRange = noRange
        ? { start: calendarDayDate, end: newEndDate }
        : determineNextRange(calendarDayDate, selectedRange);

      setSelectedRange(newRange);

      if (onChange) {
        onChange({ start: newRange.start, end: newRange.end }, difference);
      }
    },
    [selectedRange, noRange],
  );

  useEffect(() => {
    if (!determineSelectedRangeStart || !isEmptyRange(range)) {
      return;
    }

    const rangeStart = determineSelectedRangeStart();
    if (!rangeStart) {
      return;
    }

    onChange({ start: rangeStart, end: null });
  }, []);

  // If external range has been updated -- update local range too
  useEffect(() => {
    if (range.start !== selectedRange.start) {
      handleUpdateSelectedRange(range);
    }
  }, [range.start]);

  const booked = getExtraRanges()?.map((range) => ({
    from: parseDate(range.start),
    to: parseDate(range.end),
  }));

  const { lastDate, singleDates, rangeStartDates, rangeEndDates, rangeMiddleDates } = handleFilledDates({
    ranges: booked,
  });

  return (
    <div className={styles.datePicker}>
      <ResolvedDatePicker
        mode="single"
        selected={range.start}
        defaultMonth={lastDate}
        onSelect={handleDayClick}
        modifiers={{ rangeStartDates, rangeEndDates, rangeMiddleDates, singleDates }}
        modifiersClassNames={{
          rangeStartDates: datePickerStyles.filledStart,
          rangeEndDates: datePickerStyles.filledEnd,
          rangeMiddleDates: datePickerStyles.filledMiddle,
          singleDates: datePickerStyles.filledSingle,
        }}
      />
    </div>
  );
};
